import {
    Component,
    defineComponent,
    h,
    ref,
    PropType,
    Ref,
    toRaw,
    watch,
} from "vue";

import { Months } from "../../components/filters/date.jsx";
import ImageFallback from "../../components/imageFallback.js";
import Tooltip from "../../components/tooltip.js";
import {
    ContextMenuItem,
    useContextMenuProvider,
} from "../../components/contextMenuProvider.js";
import LogTypes from "../../logs/exports.js";
import { renderLog as buildLog } from "../../utils/logRenderer.js";
import {
    getMemberAvatar,
    getMemberDisplayName,
    getMemberTag,
} from "../../utils.js";
import { buildAutomateMenuItems } from "../automates/menu.jsx";
import Store from "../../store.js";
import { DefaultEntityClient } from "../../core/api/EntityClient.js";
import type { LogEntry } from "../../core/types.js";
import {
    getRolePrimaryColorCss,
    roleColorGradientToCss,
} from "../../shared/roles/roleColors.js";
import type { RoleColors } from "../../shared/roles/roleColors.js";

type I18n = (key: string) => string;
type FilterValues = Map<string, unknown>;
type MemberLike = Parameters<typeof getMemberDisplayName>[0] & { id: string };
type ChannelLike = { id: string; name: string };
type RoleLike = {
    id: string;
    name?: string;
    color?: number;
    colors?: RoleColors | null;
    icon?: string | null;
    iconURL?: string | null;
    unicodeEmoji?: string | null;
    unciodeEmoji?: string | null;
};

type LogTypeDefinition = {
    name: string;
    filter?: {
        icon?: string;
    };
    definition: (i18n: I18n, log: LogEntry) => unknown;
};

const LogTypeDefinitions = LogTypes as readonly LogTypeDefinition[];
const LogsRenderer = Object.fromEntries(
    LogTypeDefinitions.map((logType) => [
        logType.name,
        logType,
    ]),
);
const TooltipComponent = Tooltip as Component;

function getRoleDisplayName(role: RoleLike): string {
    if (role.name)
        return role.name;

    if (role.id && role.id === Store.selectedGuild?.id)
        return "@everyone";

    return `Rôle ${role.id}`;
}

function mergeRoles(...roles: Array<RoleLike | null | undefined>): RoleLike {
    const merged: Partial<RoleLike> = {};

    for (const role of roles) {
        if (!role)
            continue;

        for (const [ key, value ] of Object.entries(role)) {
            if (value !== undefined && value !== null && value !== "")
                merged[key as keyof RoleLike] = value as never;
        }
    }

    return merged as RoleLike;
}

function getRoleIconUrl(role: RoleLike): string | null {
    const icon = role.iconURL ?? role.icon;

    if (!icon || !role.id)
        return null;

    if (icon.startsWith("http://") || icon.startsWith("https://"))
        return icon;

    return `https://cdn.discordapp.com/role-icons/${role.id}/${icon}.webp?size=64`;
}

function getRoleEmoji(role: RoleLike): string | null {
    return role.unicodeEmoji ?? role.unciodeEmoji ?? null;
}

function getCurrentDayTimestamp() {
    const currentDate = new Date();

    currentDate.setHours(0, 0, 0, 0);

    return currentDate;
}

function copyToClipboard(text: string) {
    navigator.clipboard.writeText(text);
}

export const LogsList = defineComponent({
    props: {
        logs: {
            type: Array as PropType<LogEntry[]>,
            required: true,
        },
        searchError: {
            type: String as PropType<string | null>,
            default: null,
        },
        selectedLog: {
            type: Object as PropType<LogEntry | null>,
            default: null,
        },
        members: {
            type: Object as PropType<Ref<Map<string, unknown>>>,
            required: true,
        },
        channels: {
            type: Object as PropType<Ref<Map<string, unknown>>>,
            required: true,
        },
        filters: {
            type: Object as PropType<Ref<FilterValues>>,
            required: true,
        },
        i18n: {
            type: Function as PropType<I18n>,
            required: true,
        },
        onupdateFilters: {
            type: Function as PropType<(values: FilterValues) => void>,
            required: true,
        },
        onselectLog: {
            type: Function as PropType<(log: LogEntry) => void>,
            required: true,
        },
    },

    setup(props) {
        const contextMenuProvider = useContextMenuProvider();
        const refRoles = ref(new Map<string, RoleLike>());

        async function loadRoles() {
            const roles = await DefaultEntityClient.getRoles().catch(() => []);

            refRoles.value = new Map(roles.map((role) => [ role.id, role as RoleLike ]));
        }

        watch(
            () => Store.selectedGuild?.id,
            () => void loadRoles(),
            { immediate: true },
        );

        function renderUser(userId: string) {
            const member = props.members.value.get(userId) as MemberLike | undefined;

            function showUserContextMenu(event: MouseEvent) {
                event.preventDefault();
                event.stopImmediatePropagation();

                contextMenuProvider.displayContextMenu({
                    origin: {
                        x: event.pageX,
                        y: event.pageY,
                    },
                    items: [
                        {
                            icon    : () => <i class="material-symbols-outlined">person</i>,
                            label   : () => props.i18n("context_menu.logs_user.show_profil"),
                        },
                        {
                            icon    : () => <i class="material-symbols-outlined">filter_alt</i>,
                            label   : () => props.i18n("context_menu.logs_user.filter_user"),
                            onClick : () => {
                                props.filters.value.set("users", new Map([[ userId, toRaw(member) ]]));
                                props.onupdateFilters(props.filters.value);
                            },
                        },
                        {
                            icon    : () => <i class="material-symbols-outlined">filter_alt_off</i>,
                            label   : () => props.i18n("context_menu.logs_user.exclude_filter_user"),
                            onClick : () => {
                                const users = props.filters.value.get("users");

                                if (users instanceof Map)
                                    users.delete(userId);

                                props.onupdateFilters(props.filters.value);
                            },
                        },
                        {
                            icon    : () => <i class="material-symbols-outlined">link</i>,
                            label   : () => props.i18n("context_menu.copy_identifier"),
                            onClick : () => copyToClipboard(userId),
                        },
                    ],
                });
            }

            if (member)
                return h(TooltipComponent, { label: getMemberTag(member) }, () => (
                        <router-link onContextmenu={showUserContextMenu} to={`/users/${member.id}`} class="log-user">
                            <ImageFallback src={getMemberAvatar(member)} default="https://cdn.discordapp.com/embed/avatars/0.png" alt={getMemberDisplayName(member)} />
                            {getMemberDisplayName(member)}
                        </router-link>
                    ));

            return h(TooltipComponent, { label: userId }, () => (
                    <router-link onContextmenu={showUserContextMenu} to={`/users/${userId}`} class="log-user">
                        {props.i18n("page.logs.user_not_found")}
                    </router-link>
                ));
        }

        function renderChannelDirect(channelId: string, channel: ChannelLike | undefined) {
            function showChannelContextMenu(event: MouseEvent) {
                event.preventDefault();
                event.stopImmediatePropagation();

                contextMenuProvider.displayContextMenu({
                    origin: {
                        x: event.pageX,
                        y: event.pageY,
                    },
                    items: [
                        {
                            icon    : () => <i class="material-symbols-outlined">forum</i>,
                            label   : () => props.i18n("context_menu.logs_channel.show_channel"),
                        },
                        {
                            icon    : () => <i class="material-symbols-outlined">filter_alt</i>,
                            label   : () => props.i18n("context_menu.logs_channel.filter_channel"),
                            onClick : () => {
                                if (channel)
                                    props.filters.value.set("channels", new Set([ channel.id ]));

                                props.onupdateFilters(props.filters.value);
                            },
                        },
                        {
                            icon    : () => <i class="material-symbols-outlined">filter_alt_off</i>,
                            label   : () => props.i18n("context_menu.logs_channel.exclude_filter_channel"),
                            onClick : () => {
                                const channels = props.filters.value.get("channels");

                                if (channels instanceof Set && channel)
                                    channels.delete(channel.id);

                                props.onupdateFilters(props.filters.value);
                            },
                        },
                        {
                            icon    : () => <i class="material-symbols-outlined">link</i>,
                            label   : () => props.i18n("context_menu.copy_identifier"),
                            onClick : () => copyToClipboard(channel?.id ?? channelId),
                        },
                    ],
                });
            }

            if (channel)
                return h(TooltipComponent, { label: channel.id }, () => (
                        <router-link onContextmenu={showChannelContextMenu} to={`/channels/${channel.id}`} class="log-channel">
                            {channel.name}
                        </router-link>
                    ));

            return h(TooltipComponent, { label: channelId }, () => (
                    <router-link onContextmenu={showChannelContextMenu} to={`/channels/${channelId}`} class="log-channel">
                        {props.i18n("page.logs.channel_not_found")}
                    </router-link>
                ));
        }

        function renderChannel(channelId: string) {
            return renderChannelDirect(channelId, props.channels.value.get(channelId) as ChannelLike | undefined);
        }

        function renderRole(role: RoleLike) {
            const resolvedRole = mergeRoles(refRoles.value.get(role.id), role);
            const roleName = getRoleDisplayName(resolvedRole);
            const roleColor = getRolePrimaryColorCss(resolvedRole);
            const roleBackground = roleColorGradientToCss(resolvedRole);
            const roleIcon = getRoleIconUrl(resolvedRole);
            const roleEmoji = getRoleEmoji(resolvedRole);

            function showRoleContextMenu(event: MouseEvent) {
                event.preventDefault();
                event.stopImmediatePropagation();

                contextMenuProvider.displayContextMenu({
                    origin: {
                        x: event.pageX,
                        y: event.pageY,
                    },
                    items: [
                        {
                            icon    : () => <i class="material-symbols-outlined">sell</i>,
                            label   : () => props.i18n("context_menu.logs_role.show_role"),
                        },
                        {
                            icon    : () => <i class="material-symbols-outlined">link</i>,
                            label   : () => props.i18n("context_menu.copy_identifier"),
                            onClick : () => copyToClipboard(resolvedRole.id),
                        },
                    ],
                });
            }

            return (
                <router-link
                    onContextmenu={showRoleContextMenu}
                    to={`/roles/${resolvedRole.id}`}
                    class="log-channel"
                    style={{
                        background  : `color-mix(in srgb, ${roleColor} 22%, #343434)`,
                        border      : `1px solid color-mix(in srgb, ${roleColor} 55%, #343434)`,
                        color       : "#fff",
                    }}>
                    <span
                        style={{
                            width        : ".55rem",
                            height       : ".55rem",
                            flex         : "0 0 auto",
                            borderRadius : "999px",
                            marginRight  : ".4rem",
                            background   : roleBackground,
                        }} />
                    {roleIcon && (
                        <img
                            src={roleIcon}
                            alt=""
                            style={{
                                width       : "1rem",
                                height      : "1rem",
                                flex        : "0 0 auto",
                                marginRight : ".35rem",
                                objectFit   : "contain",
                            }} />
                    )}
                    {!roleIcon && roleEmoji && (
                        <span style={{ marginRight: ".35rem", lineHeight: "1" }}>
                            {roleEmoji}
                        </span>
                    )}
                    {roleName}
                </router-link>
            );
        }

        function renderRoles(roles: RoleLike[]) {
            return (
                <div class="log-role-list">
                    {roles.map(renderRole)}
                </div>
            );
        }

        function renderLogTime(log: LogEntry) {
            const date = new Date(log.createdAt);

            return h(TooltipComponent, { label: date.toDateString() }, () => (
                    <span class="log-time">
                        {date.toLocaleTimeString()}
                    </span>
                ));
        }

        function renderIcon(logType: LogTypeDefinition | undefined) {
            return logType?.filter?.icon
                ? <i class="material-symbols-outlined">{logType.filter.icon}</i>
                : <i class="material-symbols-outlined">question_mark</i>;
        }

        function showLogContextMenu(log: LogEntry, event: MouseEvent) {
            event.preventDefault();
            event.stopImmediatePropagation();

            const items: ContextMenuItem[] = [
                {
                    icon    : () => <i class="material-symbols-outlined">description</i>,
                    label   : () => props.i18n("context_menu.logs_entry.show_details"),
                    onClick : () => props.onselectLog(log),
                },
                {
                    icon    : () => <i class="material-symbols-outlined">filter_alt</i>,
                    label   : () => props.i18n("context_menu.logs_entry.filter_log_type"),
                    onClick : () => {
                        props.filters.value.set("types", new Set([ log.type ]));
                        props.onupdateFilters(props.filters.value);
                    },
                },
                {
                    icon    : () => <i class="material-symbols-outlined">filter_alt_off</i>,
                    label   : () => props.i18n("context_menu.logs_entry.exclude_filter_log_type"),
                    onClick : () => {
                        const types = props.filters.value.get("types");

                        if (types instanceof Set)
                            types.delete(log.type);

                        props.onupdateFilters(props.filters.value);
                    },
                },
            ];

            items.push(...buildAutomateMenuItems(
                log.type,
                (automate: { id: string }) => {
                    localStorage.setItem("logsCord.pendingAutomateRun", JSON.stringify({
                        automateId: automate.id,
                        seedLog: log,
                    }));
                    location.assign("/automates");
                },
                () => location.assign("/automates"),
            ) as ContextMenuItem[]);

            items.push({
                icon    : () => <i class="material-symbols-outlined">schedule</i>,
                label   : () => props.i18n("context_menu.copy_date"),
                onClick : () => {
                    const dateStr = new Date(log.createdAt).toLocaleString("fr-FR", {
                        timeZoneName    : "short",
                        hour12          : false,
                    });

                    copyToClipboard(dateStr);
                },
            });

            contextMenuProvider.displayContextMenu({
                origin: {
                    x: event.pageX,
                    y: event.pageY,
                },
                items,
            });
        }

        function renderLog(log: LogEntry) {
            const logType = LogsRenderer[log.type];

            return (
                <div
                    class={{ "log-entry": true, selected: log === toRaw(props.selectedLog) }}
                    onClick={() => props.onselectLog(log)}
                    onContextmenu={(event: MouseEvent) => showLogContextMenu(log, event)}
                    key={log.id}
                >
                    {renderLogTime(log)}
                    <span class="log-icon">{renderIcon(logType)}</span>
                    {logType
                        ? buildLog(log, logType.definition(props.i18n, log), {
                            renderUser,
                            renderChannel,
                            renderChannelDirect,
                            renderRole,
                            renderRoles,
                        })
                        : log.type}
                </div>
            );
        }

        function renderLogs() {
            if (props.searchError)
                return <div class="log-entry">{props.searchError}</div>;

            const result = [];
            let currentTimestamp = getCurrentDayTimestamp();

            for (const proxyLog of props.logs) {
                const log = toRaw(proxyLog);
                const diff = currentTimestamp.getTime() - log.createdAt;

                if (diff > 0) {
                    const dayInMilliseconds = 24 * 60 * 60 * 1000;
                    const daysElapsed = Math.floor(diff / dayInMilliseconds) + 1;
                    const timeShift = dayInMilliseconds * daysElapsed;

                    currentTimestamp = new Date(currentTimestamp.getTime() - timeShift);
                    result.push(
                        <div class="log-day-separator">
                            {currentTimestamp.getDate()} {Months[currentTimestamp.getMonth()]} {currentTimestamp.getFullYear()}
                        </div>,
                    );
                }

                result.push(renderLog(log));
            }

            return result.length
                ? result
                : <div class="log-entry">Aucune nouvelle log.</div>;
        }

        return renderLogs;
    },
});
